import { initialProfileResponse } from "./initial-profile";
/** Public availability only. Final checks explicitly opt out with no-store. */
export const AVAILABILITY_HANDOFF_TTL_MS = 1_000;
export const AVAILABILITY_CACHE_LIMIT = 80;
const STORAGE = "booking-availability-v2";
type Entry = { at: number; body: string };
const entries = new Map<string, Entry>();
// A one-second handoff shares even a queued response with the initial render.
// It retains the server freshness verbatim and never enters durable storage.
const recentResponses = new Map<
  string,
  { at: number; until: number; response: Response }
>();
export const cacheUpdates = new EventTarget();
const pending = new Map<string, Promise<Response>>();
const queue: Array<{ key: string; run: () => void; background: boolean }> = [];
let running = 0;
let generation = 0;
let pausedUntil = 0;
let wakeup: ReturnType<typeof setTimeout> | undefined;
let backgroundStarts: number[] = [];
try {
  backgroundStarts = JSON.parse(
    sessionStorage.getItem("booking-prefetch-budget") || "[]",
  ).filter((n: unknown) => typeof n === "number" && n > Date.now() - 60_000);
} catch {
  /* Optional storage. */
}
function schedulePump(delay: number) {
  if (wakeup) clearTimeout(wakeup);
  wakeup = setTimeout(
    () => {
      wakeup = undefined;
      pump();
    },
    Math.max(1, delay),
  );
}

// Durable evidence belongs to the server and is partitioned by calendar mode.
// Never restore a previous local-testing session's client-side availability.
function persist() {
  while (entries.size > AVAILABILITY_CACHE_LIMIT)
    entries.delete(entries.keys().next().value!);
  try {
    sessionStorage.removeItem(STORAGE);
  } catch {
    /* Memory still works. */
  }
}
function pump() {
  while (running < 3 && queue.length) {
    queue.sort((a, b) => Number(a.background) - Number(b.background));
    const now = Date.now();
    backgroundStarts = backgroundStarts.filter((at) => at > now - 60_000);
    if (pausedUntil > now) {
      schedulePump(pausedUntil - now);
      return;
    }
    if (queue[0]!.background && backgroundStarts.length >= 60) {
      schedulePump(backgroundStarts[0]! + 60_000 - now);
      return;
    }
    const job = queue.shift()!;
    if (job.background) {
      backgroundStarts.push(now);
      try {
        sessionStorage.setItem(
          "booking-prefetch-budget",
          JSON.stringify(backgroundStarts),
        );
      } catch {
        /* Optional storage. */
      }
    }
    running++;
    job.run();
  }
}
export function availabilityRetrySeconds() {
  return Math.max(0, Math.ceil((pausedUntil - Date.now()) / 1000));
}
function expired(entry: Entry) {
  const data = JSON.parse(entry.body);
  const recheckAt = data.bookingNotice?.recheckAt;
  return (
    data.availabilityFreshness?.complete === false ||
    Boolean(data.availabilityFreshness?.uncheckedDates?.length) ||
    Date.now() - entry.at >= AVAILABILITY_HANDOFF_TTL_MS ||
    Boolean(recheckAt && Date.parse(recheckAt) <= Date.now())
  );
}
function response(entry: Entry) {
  const stale = expired(entry);
  const data = JSON.parse(entry.body);
  if (stale)
    data.availabilityFreshness = {
      ...data.availabilityFreshness,
      state: "refreshing",
      complete: false,
      refreshAfterSeconds: 2,
    };
  return new Response(JSON.stringify(data), {
    headers: {
      "Content-Type": "application/json",
      "X-Availability-Stale": String(expired(entry)),
    },
  });
}
function requestKey(request: Request) {
  const url = new URL(request.url);
  url.searchParams.sort();
  return (
    url.href +
    "|" +
    (request.headers.get("Accept-Language") || "") +
    "|" +
    (request.headers.get("X-Booking-Route-Preview") || "")
  );
}
export function clearAvailabilityCache() {
  entries.clear();
  recentResponses.clear();
  generation++;
  pending.clear();
  persist();
}
export const availabilityFetch: typeof fetch = async (input, init) => {
  const request = new Request(input, init);
  const initial = initialProfileResponse(request);
  if (initial) return initial;
  const url = new URL(request.url);
  if (
    request.cache === "no-store" ||
    request.headers.has("x-booking-address-proof")
  ) {
    const headers = new Headers(request.headers);
    headers.set("Cache-Control", "no-store");
    return fetch(new Request(request, { headers, cache: "no-store" }));
  }
  if (
    request.method !== "GET" ||
    !url.pathname.includes("/booking/public/") ||
    !/\/(?:shared-)?availability$/.test(url.pathname) ||
    url.pathname.includes("/reservations/")
  )
    return fetch(request);
  const key = requestKey(request);
  const recent = recentResponses.get(key);
  if (
    recent &&
    Date.now() < recent.until &&
    performance.now() - recent.at < 1000
  )
    return recent.response.clone();
  recentResponses.delete(key);
  let saved = entries.get(key);
  if (
    saved &&
    JSON.parse(saved.body).bookingNotice?.recheckAt &&
    Date.parse(JSON.parse(saved.body).bookingNotice.recheckAt) <= Date.now()
  ) {
    entries.delete(key);
    saved = undefined;
  }
  if (saved && !expired(saved)) return response(saved);
  const background = request.headers.get("X-Booking-Prefetch") === "1";
  if (!saved && !background && pausedUntil > Date.now())
    return new Response(
      JSON.stringify({
        title: "Too many requests",
        status: 429,
        detail: "Please retry after the waiting period.",
      }),
      {
        status: 429,
        headers: {
          "Content-Type": "application/problem+json",
          "Retry-After": String(Math.ceil((pausedUntil - Date.now()) / 1000)),
        },
      },
    );
  if (!background) {
    const job = queue.find((job) => job.key === key);
    if (job) {
      job.background = false;
      pump();
    }
  }
  let task = pending.get(key);
  if (!task) {
    const requestedGeneration = generation;
    task = new Promise<Response>((resolve, reject) => {
      queue.push({
        key,
        background,
        run: () => {
          const headers = new Headers(request.headers);
          headers.delete("X-Booking-Prefetch");
          fetch(
            new Request(request, {
              headers,
              signal: AbortSignal.timeout(20_000),
            }),
          )
            .then(async (result) => {
              if (result.status === 429 || result.headers.has("Retry-After")) {
                const value = result.headers.get("Retry-After");
                const seconds =
                  value && /^\d+$/.test(value)
                    ? Number(value)
                    : value
                      ? Math.ceil((Date.parse(value) - Date.now()) / 1000)
                      : 60;
                pausedUntil =
                  Date.now() +
                  Math.max(1, Number.isFinite(seconds) ? seconds : 60) * 1000;
              }
              if (result.ok) {
                const body = await result.clone().text();
                const data = JSON.parse(body);
                if (requestedGeneration === generation) {
                  if (recentResponses.size >= 80)
                    recentResponses.delete(
                      recentResponses.keys().next().value!,
                    );
                  recentResponses.set(key, {
                    at: performance.now(),
                    until: Math.min(
                      Date.now() + 1000,
                      Date.parse(data.bookingNotice?.recheckAt) || Infinity,
                    ),
                    response: result.clone(),
                  });
                }
                if (
                  requestedGeneration === generation &&
                  Array.isArray(data.slots) &&
                  !data.error &&
                  (!data.availabilityFreshness ||
                    (data.availabilityFreshness.state === "current" &&
                      data.availabilityFreshness.complete !== false &&
                      !data.availabilityFreshness.uncheckedDates?.length)) &&
                  data.complete !== false &&
                  !data.slots.some(
                    (slot: { travelStatus?: string }) =>
                      slot.travelStatus === "pending",
                  )
                ) {
                  entries.delete(key);
                  entries.set(key, { at: Date.now(), body });
                  persist();
                }
              }
              if (result.ok) {
                const value = await result.clone().json();
                if (
                  value.availabilityFreshness &&
                  value.availabilityFreshness.state !== "current"
                ) {
                  const headers = new Headers(result.headers);
                  headers.set("X-Availability-Stale", "true");
                  queueMicrotask(() =>
                    cacheUpdates.dispatchEvent(
                      new CustomEvent("refresh", {
                        detail: {
                          key,
                          state:
                            value.availabilityFreshness.state === "error"
                              ? "failed"
                              : "refreshing",
                        },
                      }),
                    ),
                  );
                  resolve(
                    new Response(await result.text(), {
                      status: result.status,
                      headers,
                    }),
                  );
                  return;
                }
              }
              resolve(result);
            })
            .catch(reject)
            .finally(() => {
              if (pending.get(key) === task) pending.delete(key);
              running--;
              pump();
            });
        },
      });
    });
    pending.set(key, task);
    pump();
  }
  if (saved) {
    queueMicrotask(() =>
      cacheUpdates.dispatchEvent(
        new CustomEvent("refresh", { detail: { key, state: "refreshing" } }),
      ),
    );
    void task.then(
      (result) =>
        cacheUpdates.dispatchEvent(
          new CustomEvent("refresh", {
            detail: {
              key,
              state: result.ok
                ? result.headers.get("X-Availability-Stale") === "true"
                  ? "refreshing"
                  : "updated"
                : "failed",
            },
          }),
        ),
      () =>
        cacheUpdates.dispatchEvent(
          new CustomEvent("refresh", { detail: { key, state: "failed" } }),
        ),
    );
    return response(saved);
  }
  // Subscribers do not cancel the shared transport. Navigation guards discard late results.
  const result = task.then((result) => result.clone());
  if (!request.signal) return result;
  return new Promise<Response>((resolve, reject) => {
    const abort = () =>
      reject(
        request.signal.reason ?? new DOMException("Aborted", "AbortError"),
      );
    if (request.signal.aborted) {
      abort();
    }
    request.signal.addEventListener("abort", abort, { once: true });
    result
      .then(resolve, reject)
      .finally(() => request.signal.removeEventListener("abort", abort));
  });
};
